import React, { useCallback, useEffect, useRef, useState } from "react";
import DeckBimViewer from "./DeckBimViewer/DeckBimViewer";
import type {
  IPixBimCameraState,
  IPixCircle,
  IPixDimensionLine,
  IPixPath,
  IPixPolygon,
  PixBimViewerAction,
} from "./DeckBimViewer/types";
import { invertTransform } from "./DeckBimViewer/transformUtils";
import { Viewer3d } from "./Viewer3d";
import { ViewerModeButtonBar } from "./ViewerModeButtonBar";
import type { IViewPoint2d, ViewerSyncState } from "./viewerSyncTypes";

type MultiViewerDisplayMode = "ONLY_2D" | "ONLY_3D" | "BOTH_2D_3D";
export type MultiViewerFragFileRef = {
  fileName: string;
  frag: {
    blobName?: string;
    url: string;
    sizeBytes?: number;
  };
  manifest: {
    blobName?: string;
    sizeBytes?: number;
  };
};

export interface MultiViewerProps {
  twoDOnly?: boolean;
  twoD: {
    polygons: IPixPolygon[];
    paths?: IPixPath[];
    circles?: IPixCircle[];
    dimensionLines?: IPixDimensionLine[];
    angle?: number;
    action?: PixBimViewerAction;
    zoomExtentKey?: string | number | null;
    transformPivot?: { x: number; y: number } | null;
    useLeftClickToPan?: boolean;
    flip2dCoordinate?: boolean;
    initialCamera?: {
      zoom: number;
      centerWorld: { x: number; y: number };
    };
    onCameraChange?: (camera: IPixBimCameraState) => void;
    onMoveStart?: (id: string) => void;
    onRightMenuClick?: (action: string, polygon: IPixPolygon) => void;
    onClick?: (id: string | undefined, coordinates: [number, number]) => void;
    onDragAndDrop?: (id: string, points: number[][]) => void;
    onDragSelected?: (ids: string[]) => void;
  };
  threeD: {
    fragFiles: MultiViewerFragFileRef[];
    isLoadingFragFiles?: boolean;
    isFetchingFragFiles?: boolean;
    fragFilesError?: unknown;
    visibleMcbimFileNames: string[];
    clipZMm?: { bottom: number; top: number } | null;
  };
}

export const MultiViewer: React.FC<MultiViewerProps> = ({
  twoDOnly = false,
  twoD,
  threeD,
}) => {
  const {
    polygons,
    paths = [],
    circles = [],
    dimensionLines = [],
    angle = 180,
    action = "normal",
    zoomExtentKey,
    transformPivot,
    useLeftClickToPan = false,
    flip2dCoordinate = true,
    initialCamera,
    onCameraChange,
    onMoveStart,
    onRightMenuClick,
    onClick,
    onDragAndDrop,
    onDragSelected,
  } = twoD;
  const {
    fragFiles,
    isLoadingFragFiles,
    isFetchingFragFiles,
    fragFilesError,
    visibleMcbimFileNames,
    clipZMm,
  } = threeD;
  const [displayMode, setDisplayMode] =
    useState<MultiViewerDisplayMode>("ONLY_2D");
  const [hideIfcCovering, setHideIfcCovering] = useState(true);
  const effectiveDisplayMode: MultiViewerDisplayMode = twoDOnly
    ? "ONLY_2D"
    : displayMode;
  const show2d =
    effectiveDisplayMode === "ONLY_2D" || effectiveDisplayMode === "BOTH_2D_3D";
  const show3d =
    effectiveDisplayMode === "ONLY_3D" || effectiveDisplayMode === "BOTH_2D_3D";
  const split = show2d && show3d;

  const [syncState, setSyncState] = useState<ViewerSyncState>({
    syncViewports: false,
    viewPoint2d: null,
    cameraDirection: [0, 0, -1],
    distance3d: 50,
  });
  const last3dCameraRef = useRef("");
  const last3dCameraPublishedAtRef = useRef(0);
  const lastViewPoint2dRef = useRef("");
  const lastViewPoint2dPublishedAtRef = useRef(0);

  const angleRef = useRef(angle);
  const flip2dCoordinateRef = useRef(flip2dCoordinate);
  const transformPivotRef = useRef(transformPivot);
  useEffect(() => {
    angleRef.current = angle;
  }, [angle]);
  useEffect(() => {
    flip2dCoordinateRef.current = flip2dCoordinate;
  }, [flip2dCoordinate]);
  useEffect(() => {
    transformPivotRef.current = transformPivot;
  }, [transformPivot]);
  const [applyInitialCamera, setApplyInitialCamera] = useState(
    Boolean(initialCamera),
  );

  useEffect(() => {
    if (!initialCamera) {
      setApplyInitialCamera(false);
      return;
    }
    setApplyInitialCamera(true);
  }, [initialCamera]);

  useEffect(() => {
    if (!applyInitialCamera) return;
    if (!polygons.length) return;

    const id = window.setTimeout(() => setApplyInitialCamera(false), 1000);
    return () => window.clearTimeout(id);
  }, [applyInitialCamera, polygons.length]);

  const handleViewPoint2dChange = useCallback(
    (camera: IPixBimCameraState) => {
      if (camera.zoom == null || !camera.centerWorld) return;
      onCameraChange?.(camera);

      const pivot = transformPivotRef.current;
      const angleDeg = angleRef.current;
      const shouldFlip2dCoordinate = flip2dCoordinateRef.current;
      const [ifcX, ifcY] = pivot
        ? invertTransform(
            [camera.centerWorld.x, camera.centerWorld.y],
            [pivot.x, pivot.y],
            angleDeg,
            shouldFlip2dCoordinate,
          )
        : [camera.centerWorld.x, camera.centerWorld.y];

      const vpKey = `${ifcX.toFixed(2)},${ifcY.toFixed(2)},${camera.zoom.toFixed(2)}`;
      const now = performance.now();
      if (
        lastViewPoint2dRef.current !== vpKey &&
        now - lastViewPoint2dPublishedAtRef.current > 100
      ) {
        lastViewPoint2dRef.current = vpKey;
        lastViewPoint2dPublishedAtRef.current = now;
        const viewPoint2d: IViewPoint2d = { x: ifcX, y: ifcY };
        setSyncState((prev) => ({ ...prev, viewPoint2d }));
      }
    },
    [onCameraChange],
  );

  const handle3dCameraChange = useCallback(
    (cameraDirection: [number, number, number], distance3d: number) => {
      const key = `${cameraDirection.map((value) => value.toFixed(3)).join(",")}:${distance3d.toFixed(2)}`;
      const now = performance.now();
      if (
        last3dCameraRef.current === key ||
        now - last3dCameraPublishedAtRef.current < 100
      ) {
        return;
      }
      last3dCameraRef.current = key;
      last3dCameraPublishedAtRef.current = now;
      setSyncState((prev) => ({ ...prev, cameraDirection, distance3d }));
    },
    [],
  );

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {!twoDOnly && (
        <ViewerModeButtonBar
          displayMode={displayMode}
          onDisplayModeChange={setDisplayMode}
          syncViewports={syncState.syncViewports}
          onSyncViewportsChange={(syncViewports) =>
            setSyncState((prev) => ({ ...prev, syncViewports }))
          }
          showIfcCoveringToggle={show3d}
          hideIfcCovering={hideIfcCovering}
          onHideIfcCoveringChange={setHideIfcCovering}
        />
      )}
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: split ? "row" : "column",
          gap: split ? 1 : 0,
          background: split ? "#ced4da" : undefined,
        }}
      >
        {show2d && (
          <div
            style={{
              height: "100%",
              width: split ? "50%" : "100%",
              minWidth: 0,
            }}
          >
            <DeckBimViewer
              useLeftClickToPan={useLeftClickToPan}
              flip2dCoordinate={flip2dCoordinate}
              angle={angle}
              polygons={polygons}
              circles={circles}
              paths={paths}
              dimensionLines={dimensionLines}
              action={action}
              zoomExtentKey={zoomExtentKey}
              onMoveStart={onMoveStart}
              onRightMenuClick={onRightMenuClick}
              onClick={onClick}
              onDragAndDrop={onDragAndDrop}
              onDragSelected={onDragSelected}
              {...(applyInitialCamera && initialCamera ? initialCamera : {})}
              onCameraChange={handleViewPoint2dChange}
            />
          </div>
        )}
        {show3d && (
          <div
            style={{
              height: "100%",
              width: split ? "50%" : "100%",
              minWidth: 0,
            }}
          >
            <Viewer3d
              fragFiles={fragFiles}
              isLoadingFragFiles={isLoadingFragFiles}
              isFetchingFragFiles={isFetchingFragFiles}
              fragFilesError={fragFilesError}
              visibleMcbimFileNames={visibleMcbimFileNames}
              clipZMm={clipZMm}
              hideIfcCovering={hideIfcCovering}
              syncState={syncState}
              onCameraChange={handle3dCameraChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};
